/**
 * Global annotation-pipeline counts strip per `feature-spec.md` §3.3.
 *
 * Four cells with arrows between them:
 *   Awaiting annotation  →  1st annotator complete
 *                           →  Agreed / Disputed     →  In golden dataset
 *
 * The `annotated_both` bucket ("Both complete") was dropped from the
 * strip — `useSubmitAnnotation` computes agreement inline on the second
 * pass and flips the record straight to `agreed` / `disputed` in the
 * same transaction. No record ever rests at `annotated_both`, so the
 * cell sat at 0 indefinitely and was visual noise. The BE dropped the
 * `bothComplete` field from the response in lockstep — the transient
 * state now buckets under `agreedDisputed` server-side.
 *
 * Wired to `usePipelineMetrics` (GET /v1/golab/annotation/pipeline-metrics,
 * B18-BE). Counts auto-refresh when annotation, resolution, claim, or
 * upload mutations invalidate `annotationKeys.pipelineMetrics()`. During
 * loading or error we render em-dash placeholders so the layout stays
 * stable (no skeleton flash, no inline error banner — the strip is an
 * ambient indicator, not a primary surface).
 */
import React from 'react';
import { ChevronRight } from 'lucide-react';

import { Card } from '../../../../../ui/card';
import { cn } from '../../../../../../lib/utils';
import { usePipelineMetrics } from '../../hooks/useAnnotation';
import type { PipelineMetricsResponse } from '../../types';

interface Stage {
  key: string;
  label: string;
}

const STAGES: Stage[] = [
  { key: 'awaiting',        label: 'Awaiting annotation' },
  { key: 'first',           label: '1st annotator complete' },
  { key: 'agreed_disputed', label: 'Agreed / Disputed' },
  { key: 'golden',          label: 'In golden dataset' },
];

// Maps the stable testid-keys above onto BE response fields. Keep these
// separate so the DOM contract (`data-testid="pipeline-cell-<key>"`)
// doesn't drift if the API shape ever changes.
const STAGE_TO_RESPONSE_KEY: Record<string, keyof PipelineMetricsResponse> = {
  awaiting: 'awaiting',
  first: 'firstComplete',
  agreed_disputed: 'agreedDisputed',
  golden: 'golden',
};

const PLACEHOLDER = '—';

const PipelineCountsStrip: React.FC = () => {
  const { data, isLoading, isError } = usePipelineMetrics();

  const renderCount = (stageKey: string): string => {
    if (isLoading || isError || !data) return PLACEHOLDER;
    // Default to `0` if the BE drops a key — better than a runtime
    // crash on `.toLocaleString()` of undefined.
    const value = data[STAGE_TO_RESPONSE_KEY[stageKey]] ?? 0;
    return value.toLocaleString();
  };

  return (
    <section
      aria-label="Annotation pipeline counts"
      data-testid="pipeline-counts-strip"
    >
      <div className="flex flex-wrap items-stretch gap-2 md:flex-nowrap">
        {STAGES.map((stage, idx) => (
          <React.Fragment key={stage.key}>
            <Card
              className={cn(
                'flex-1 min-w-[140px] p-3',
                'flex flex-col items-center justify-center gap-1 text-center',
              )}
              data-testid={`pipeline-cell-${stage.key}`}
            >
              <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {stage.label}
              </div>
              <div className="text-2xl font-bold tabular-nums text-foreground">
                {renderCount(stage.key)}
              </div>
            </Card>
            {idx < STAGES.length - 1 && (
              <div
                className="hidden items-center justify-center text-muted-foreground md:flex"
                aria-hidden="true"
              >
                <ChevronRight className="h-5 w-5" />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </section>
  );
};

export default PipelineCountsStrip;
